import React, { useCallback, useMemo } from 'react';
import EDGrid from '../../../../BaseComponents/GridComponents/EDGrid';
import EDDraggableChipList, {
  IItem,
} from '../../../../BaseComponents/Chip/DraggableChip/EDDraggableChipList';
import EDTypography from '../../../../BaseComponents/Typography/EDTypography';
import EDButton from '../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../BaseComponents/Icon/EDIcon';
import { IColumnDef } from '../../../../Types/ColumnDefType';
import { EDDialogCard } from '../../../../BaseComponents/Card/DialogCard/EDDialogCard';
import LockedChipList from '../../../../BaseComponents/Chip/LockedChip/EDLockedChipList';
import CheckboxSelectList from '../CheckboxSelectList/CheckboxSelectList';
import { makeStyles } from 'tss-react/mui';
import EDTreeSelect from '../../../../BaseComponents/TreeSelect/EDTreeSelect';
import {
  ICheckedItems,
  ITreeSection,
} from '../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import { ISectionData } from '../../KolumnValjare';

interface IProps {
  headerColumns: readonly IColumnDef[];
  pickedColumns: readonly IColumnDef[];
  unpickedColumns: ITreeSection<ISectionData>[];
  setUnpickedColumns: (columns: ITreeSection<ISectionData>[]) => void;
  setActiveColumns: (columns: IColumnDef[]) => void;
  selectionUpdate: (
    sections: ITreeSection[],
    section: ITreeSection,
    checked: boolean,
    suggestion?: ITreeSection
  ) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
}

const useStyles = makeStyles()({
  titleText: {
    fontWeight: 700,
  },
  mainContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  flexContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  flexCenterContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  containerSpacing: {
    marginBottom: 8,
  },
} as const);

const KolumnvaljareLists: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { selectionUpdate, setActiveColumns } = props;
  const { classes } = useStyles();
  const handleSelectionUpdate = useCallback(
    (
      sections: ITreeSection[],
      checkedItems: ICheckedItems,
      checked: boolean,
      suggestion?: ITreeSection
    ) => {
      selectionUpdate(sections, checkedItems.checkedItem, checked, suggestion);
      return sections;
    },
    [selectionUpdate]
  );

  const rensaAlla = useCallback(() => setActiveColumns([]), [setActiveColumns]);

  const onItemsUpdate = useCallback(
    (items: IItem[]) => {
      let newColumns: IColumnDef[] = [];
      items.forEach((x: IItem) => {
        const newColumn = props.pickedColumns.find(
          y => y.attributeDef === x.id
        );
        if (newColumn) {
          newColumns.push(newColumn);
        }
      });
      setActiveColumns(newColumns);
    },
    [props.pickedColumns, setActiveColumns]
  );

  return (
    <EDGrid className={classes.mainContainer} container>
      <EDGrid className={classes.flexContainer} item xs={12} sm={6}>
        <div className={classes.containerSpacing}>
          <EDTypography className={classes.titleText} gutterBottom>
            Obligatoriska kolumner
          </EDTypography>
          <EDDialogCard>
            <LockedChipList
              items={useMemo(
                () =>
                  props.headerColumns.map(x => ({
                    nameLabel: x.attributeDef,
                    name: x.caption || '',
                  })),
                [props.headerColumns]
              )}
            />
          </EDDialogCard>
        </div>
        <div className={classes.flexCenterContainer}>
          <EDTypography bold>Aktiva kolumner</EDTypography>
          <EDButton
            label="Rensa alla"
            icon={<EDIcon color="primary" iconType="clear_all" />}
            onClick={rensaAlla}
            flat
            primary
            padding={1}
            labelNotUpperCase
            lowEmphasis
          />
        </div>
        <EDDialogCard testId="AktivaKolumner">
          <EDDraggableChipList
            items={useMemo(
              () =>
                props.pickedColumns.map((sum, index) => ({
                  id: sum.attributeDef,
                  name: sum.caption || '',
                })),
              [props.pickedColumns]
            )}
            onItemsUpdate={onItemsUpdate}
            dragHandle
            canDelete
          />
        </EDDialogCard>
      </EDGrid>
      <EDGrid className={classes.flexContainer} item xs={12} sm={6}>
        <EDTypography className={classes.titleText} gutterBottom>
          Valbara kolumner
        </EDTypography>
        <EDDialogCard padding={0} displayFlex>
          <CheckboxSelectList
            searchValue={props.searchValue}
            setSearchValue={props.setSearchValue}
          >
            <EDTreeSelect
              sections={props.unpickedColumns}
              sectionsUpdate={props.setUnpickedColumns}
              onCheck={handleSelectionUpdate}
              searchValue={props.searchValue}
              disableSectionsClick
            />
          </CheckboxSelectList>
        </EDDialogCard>
      </EDGrid>
    </EDGrid>
  );
};

export default KolumnvaljareLists;
